import React, { useEffect, useState, useMemo } from 'react'
import classes from './index.module.css'
import Loader from '../../../Components/GeneralComponents/Loader'
import { Wrapper } from '../../../Components/page/Wrapper'
import { Header } from '../../../Components/page/Header'
import { HeaderInfo } from '../../../Components/page/HeaderInfo'
import { Actions } from '../../../Components/page/Actions'
import { ButtonOutline } from '../../../Components/GeneralComponents/ButtonOutline/ButtonOutline'

import PageTitle from '../components/PageTitle'
import { useParams } from 'react-router-dom'
import { useGetOrgDetails } from '../hooks/useGetOrgDetails'
import { useGetOrganizationGroups } from '../hooks/useGetOrganizationGroups'
import { useGetOrgUsers } from '../hooks/useGetOrgUsers'
import UsersTable from '../components/UsersTable'
import useAdminAccess from '../../../services/hooks/useAdminAccess'
import { getUserRole } from '../../../services/helpers/getUserRole.'
import { groupRoles, orgRoles } from '../../../constants/roles'
import { useFetchAccount } from '../../Organization/hooks'
import OrganizationsPopup from '../components/OrganizationsPopup'
import usePopup from '../hooks/usePopup'
import AddMembersPopupForm from '../components/AddMembersPopupForm'
import { useDeleteOrganizationUser } from '../hooks/useDeleteOrganizationUser'
import { useResendInvite } from '../hooks/useResendInvite'
import successToast from '../../../Components/Toasts/SuccessToast'
import errorToast from '../../../Components/Toasts/ErrorToast'
import { useQueryClient } from '@tanstack/react-query'
import { useChangeOrganizationUsersRole } from '../hooks/useChangeOrganizationUsersRole'
import { Navigation } from '../../../Components/page/Navigation'
import { renderBreadcrumbs } from './breadcrumbs'
import useAccessLevel from '../hooks/useAccessLevel'
import DebounceSearch from '../../../Components/GeneralComponents/DebounceSearch/DebounceSearch'
import { useOrganizationsStore } from '../../../Storage/organizationsPage/useOrganizationsStore'
import { useAPI } from '../../../Contexts/APIContext'

export default function OrganizationMembers() {
  const { id } = useParams()

  const { isInternalAdmin, hasOrgAdminAccess } = useAccessLevel({
    o_f_id: id,
  })

  const {
    openPopup: addAdminOpenPopup,
    closePopup: addAdminClosePopup,
    isOpen: addAdminIsOpen,
  } = usePopup()

  const queryClient = useQueryClient()

  const {
    data: userData,
    isError: userDataError,
    isLoading: userDataIsLoading,
    isFetching: userDataIsFetching,
  } = useFetchAccount()

  const currentUser = userData?.user
  const currentUserRole = getUserRole(currentUser) || ''
  const { hasFullAdminAccess } = useAdminAccess(currentUser)

  const {
    data: orgDetails,
    isError: orgDetailsError,
    isLoading: orgDetailsIsLoading,
    isFetching: orgDetailsFetching,
  } = useGetOrgDetails({
    id,
  })

  const {
    data: organizationGroups,
    isError: organizationGroupsError,
    isLoading: organizationGroupsIsLoading,
    isFetching: isOrganizationGroupsFetching,
  } = useGetOrganizationGroups({
    o_f_id: id,
  })

  const {
    data: orgUsers,
    isError: orgUsersError,
    isLoading: orgUsersIsLoading,
    isFetching: orgUsersIsFetching,
  } = useGetOrgUsers({
    o_f_id: id,
  })

  const inputValue = useOrganizationsStore(state => state.inputValue)
  const setInputValue = useOrganizationsStore(state => state.setInputValue)
  const searchTerm = useOrganizationsStore(state => state.searchTerm)
  const setSearchTerm = useOrganizationsStore(state => state.setSearchTerm)

  const groups = organizationGroups?.organization_groups

  const users = useMemo(() => orgUsers?.organization_users || [], [orgUsers])

  const [filteredUsers, setFilteredUsers] = useState([])

  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      const filtered = users.filter(u => {
        const name = u.name
        const email = u.email

        if (name && name.toLowerCase().includes(term)) {
          return true
        }

        if (email && email.toLowerCase().includes(term)) {
          return true
        }

        return false
      })

      setFilteredUsers(filtered)
    } else {
      setFilteredUsers(users)
    }
  }, [searchTerm, users])

  const { mutate: deleteOrganizationUser } = useDeleteOrganizationUser()

  const handleDeleteOrganizationUser = data => {
    deleteOrganizationUser(data, {
      onSuccess: () => {
        successToast('User removed')
        queryClient.invalidateQueries(['organization'])
      },
      onError: error => {
        errorToast('Error removing User', error)
      },
    })
  }

  const { resendInvite, canResendInvite } = useResendInvite({ o_f_id: id })

  const { mutate: changeOrganizationUsersRole } =
    useChangeOrganizationUsersRole()

  const { getOrganizationUser } = useAPI()

  const handleDeleteGroupUser = async (data, isChangeRole = false) => {
    const removeData = {
      ...data,
      o_f_id: id,
      _action: 'delete',
      role:
        data.role === 'Team Administrator' ? 'Group Administrator' : data.role,
    }
    const u_f_id = data.organization_users[0]
    const user = await getOrganizationUser({ o_f_id: id, u_f_id: u_f_id })
    const groupsCountBeforeDeletion = user.organization_groups.length
    changeOrganizationUsersRole(removeData, {
      onSuccess: () => {
        queryClient.invalidateQueries(['organization'])
        if (isChangeRole) {
          successToast('Access level was successfully updated')
        } else {
          if (groupsCountBeforeDeletion > 1) {
            successToast(
              `The user was removed from this team but still has access to the organization through other ${orgDetails.name} teams or direct Organization membership. To fully remove them, an Organization Admin or Owner must take further action.`
            )
          } else {
            successToast(
              `The user was removed from this team and no longer has access to any artists, but still belongs to the ${orgDetails.name} organization. To fully remove them, an Organization Admin or Owner must take further action.`
            )
          }
        }
      },
      onError: error => {
        errorToast('Error updating User', error)
      },
    })
  }
  const handleChangeOrganizationUsersRole = data => {
    const { oldRole, ...rest } = data
    const newData = {
      ...rest,
      o_f_id: id,
      _action: 'add',
      role:
        rest.role === 'Team Administrator' ? 'Group Administrator' : rest.role,
    }
    changeOrganizationUsersRole(newData, {
      onSuccess: () => {
        handleDeleteGroupUser(
          {
            role: oldRole,
            organization_users: newData.organization_users,
            g_f_id: newData.g_f_id,
          },
          true
        )
      },
      onError: error => {
        errorToast('Error updating User', error)
      },
    })
  }

  if (
    userDataIsLoading ||
    orgDetailsIsLoading ||
    organizationGroupsIsLoading ||
    orgUsersIsLoading
  )
    return (
      <Loader
        isLoading={
          userDataIsLoading ||
          orgDetailsIsLoading ||
          organizationGroupsIsLoading ||
          orgUsersIsLoading
        }
      />
    )

  if (
    orgDetailsError ||
    organizationGroupsError ||
    orgUsersError ||
    userDataError
  ) {
    return (
      <Wrapper className={classes.wrapper}>
        <Header>
          <HeaderInfo>
            <PageTitle title={'Error loading data'} />
          </HeaderInfo>
        </Header>
      </Wrapper>
    )
  }

  return (
    <Wrapper className={classes.wrapper}>
      <OrganizationsPopup
        isOpen={addAdminIsOpen}
        closePopup={addAdminClosePopup}
      >
        <AddMembersPopupForm
          closePopup={addAdminClosePopup}
          organization={orgDetails}
          groups={groups}
        />
      </OrganizationsPopup>
      <Header>
        {orgDetails ? (
          <Navigation breadcrumbs={renderBreadcrumbs(orgDetails)} />
        ) : null}
        <HeaderInfo>
          <PageTitle title={orgDetails?.name} />
          <Actions>
            <ButtonOutline
              disable={!isInternalAdmin && !hasOrgAdminAccess}
              color={'pink'}
              size="medium"
              text={'ADD MEMBER'}
              onClick={addAdminOpenPopup}
            />
          </Actions>
        </HeaderInfo>
      </Header>
      <main className={classes.main}>
        {users?.length ? (
          <div className={classes.section}>
            <div className={classes.section__header}>
              <h2 className={classes.section__title}>Members</h2>
              <div className={classes.section__searchBar}>
                <DebounceSearch
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  searchValue={searchTerm}
                  setSearchValue={setSearchTerm}
                  placeholder={'Search by Name, Email…'}
                />
              </div>
            </div>
            <div className={classes.users}>
              {filteredUsers && (
                <UsersTable
                  isCurrentUserAnAdmin={hasFullAdminAccess}
                  currentUserRole={currentUserRole}
                  users={filteredUsers}
                  getUserRole={getUserRole}
                  optionsOrg={orgRoles}
                  optionsGroup={groupRoles}
                  onChangeRole={handleChangeOrganizationUsersRole}
                  onTeamUserDelete={handleDeleteGroupUser}
                  onOrgUserDelete={handleDeleteOrganizationUser}
                  onResendInvite={resendInvite}
                  canResendInvite={canResendInvite}
                />
              )}
            </div>
          </div>
        ) : null}
      </main>
    </Wrapper>
  )
}
